import React, { Fragment, useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import TransactionLineItemQuantity from 'components/PaymentReq/TransactionDetail/TransactionLineItemQuantity';
import { colors, paddingSize } from 'styles/cp';
import { SaleLine } from 'types/Sale';

type TransactionLineItemProps = {
  lineItem: SaleLine;
  currency?: string;
};

type _TxnLineItemPriceBreakdownProps = {
  lineItem: SaleLine;
  currency?: string;
};

function _PriceBreakdown({ lineItem, currency }: _TxnLineItemPriceBreakdownProps): JSX.Element {
  const rate =
    lineItem.rate && lineItem.rate.moneyValue ? lineItem.rate.moneyValue : lineItem.amount;
  return (
    <span>
      <FormattedNumber value={rate} currency={currency} style="currency" />{' '}
      <FormattedMessage id={'TXN_LINE_DETAIL_ITEM_PRICE_BREAKDOWN'} defaultMessage={'each'} />
    </span>
  );
}

function TransactionLineItem({ lineItem, currency }: TransactionLineItemProps): JSX.Element {
  const totalItemCost = useMemo(() => {
    if (!lineItem.rate || !lineItem.quantity) return 0;
    return lineItem.rate.moneyValue * lineItem.quantity;
  }, [lineItem.rate, lineItem.quantity]);

  return (
    <Fragment>
      <li className="line-item">
        <span className="line-item-name">{lineItem.description}</span>
        <span className="line-item-priceBreakdown">
          <_PriceBreakdown lineItem={lineItem} currency={currency} />
        </span>
        <span className="line-item-quantity">
          <TransactionLineItemQuantity itemLine={lineItem} />
        </span>
        <span className="line-item-price">
          <FormattedNumber value={totalItemCost} style="currency" currency={currency} />
        </span>
      </li>
      <style jsx>{`
        .line-item {
          border-top: 1px solid ${colors.gray05};
          padding: ${paddingSize.xs} ${paddingSize.xs};
          display: grid;
          column-gap: ${paddingSize.xs};
          align-items: center;
          grid-template-rows: max-content max-content;
          grid-template-columns: 0px 120px 40px 75px;
          grid-template-areas:
            'removeBtn name quantity price'
            'removeBtn priceBreakdown quantity price';
          &:last-of-type {
            border-bottom: 1px solid ${colors.gray05};
          }
        }
        .line-item-name {
          grid-area: name;
        }
        .line-item-priceBreakdown {
          grid-area: priceBreakdown;
          color: ${colors.gray03};
        }
        .line-item-quantity {
          grid-area: quantity;
        }
        .line-item-price {
          grid-area: price;
          text-align: right;
        }
      `}</style>
    </Fragment>
  );
}

export default TransactionLineItem;
